/**
 * Einen Wert an einen Datenpunkt schreiben (HM-110, ADR 0032).
 *
 * **Ein Auftrag, keine Zustandsänderung.** Der Knopf schickt einen Wert auf
 * die Leitung und bekommt `202` zurück; ob das Gerät ihn übernommen hat, sagt
 * die Quittung im Verlauf der Aufträge. Deshalb heißt der Knopf „Setzen …" mit
 * den drei Punkten — es kommt eine Rückfrage —, und nach dem Absenden steht
 * ein Satz, wo die Antwort zu finden ist. Ein Wert, der stillschweigend
 * „gesetzt" aussieht, wäre der Eindruck, gegen den die Gegenrichtung gebaut
 * wurde (HM-040).
 *
 * **Der Knopf erscheint nur, wo das Gerät Schreiben gemeldet hat**
 * (`access: "write" | "read_write"`, numerisch). Fähigkeiten statt Versuch
 * (Regel 10): Ein Knopf, der an einem Zählerstand ein `409` erntete, brächte
 * dem Nutzer eine Fehlermeldung für eine Frage, die er nie hätte stellen
 * sollen.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Dialog, Input } from "@hausmeter/ui";
import type { ApiComponents } from "@hausmeter/sdk";
import { api } from "../lib/client.js";
import { apiErrorMessage } from "../lib/api-error.js";
import { useSiteAccess } from "../lib/site-access.js";

type Datapoint = ApiComponents["schemas"]["Datapoint"];

/**
 * Der Schreibauftrag als Mutation — **ein** Weg für die Datenpunktliste und
 * den Wallbox-Abschnitt. Nach dem Absenden werden Aufträge, Werte und der
 * Wallbox-Zustand neu geholt: Der Auftrag steht sofort im Verlauf, der Wert
 * folgt mit der nächsten Meldung.
 */
export function useWriteDatapoint(deviceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ datapointId, value }: { datapointId: string; value: number }) => {
      const { data, error } = await api.POST("/devices/{deviceId}/datapoints/{datapointId}/value", {
        params: { path: { deviceId, datapointId } },
        body: { value },
      });
      if (error || !data)
        throw new Error(apiErrorMessage(error) ?? "Der Auftrag ging nicht hinaus.");
      return data;
    },
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ["devices", "commands", deviceId] }),
        client.invalidateQueries({ queryKey: ["devices", "wallbox", deviceId] }),
        client.invalidateQueries({ queryKey: ["devices", "evcc-site", deviceId] }),
      ]);
    },
  });
}

export function canWriteDatapoint(point: Pick<Datapoint, "access" | "valueType">): boolean {
  return (
    point.valueType === "number" && (point.access === "write" || point.access === "read_write")
  );
}

/** „Setzen …" an einer Datenpunktzeile — mit Dialog, weil der Wert eine Eingabe ist. */
export function DatapointWriteButton({
  deviceId,
  point,
  current,
}: {
  deviceId: string;
  point: Datapoint;
  /** Der zuletzt gemeldete Wert, als Vorbelegung. */
  current: number | null;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [sent, setSent] = useState(false);
  const write = useWriteDatapoint(deviceId);
  // Was die Teilung nicht erlaubt, fehlt (HM-160, Regel 10).
  const rights = useSiteAccess();

  const value = Number(draft.replace(",", "."));
  const valid = draft.trim().length > 0 && Number.isFinite(value);

  const openDialog = () => {
    setDraft(current === null ? "" : String(current));
    setSent(false);
    write.reset();
    setOpen(true);
  };

  if (!rights.canWrite) return null;

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        onClick={(event) => {
          // Die Zeile darunter führt zum Verlauf — der Knopf soll nicht dorthin.
          event.stopPropagation();
          openDialog();
        }}
      >
        Setzen …
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={`${point.name} setzen`}
        description={`Der Wert geht als Auftrag an das Gerät${point.unit === null ? "" : `, in ${point.unit}`}. Ob es ihn übernimmt, steht in der Quittung.`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              {sent ? "Schließen" : "Abbrechen"}
            </Button>
            {!sent && (
              <Button
                variant="primary"
                disabled={!valid || write.isPending}
                onClick={() =>
                  write.mutate({ datapointId: point.id, value }, { onSuccess: () => setSent(true) })
                }
              >
                {write.isPending ? "Wird gesendet …" : "Absenden"}
              </Button>
            )}
          </>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
          <Input
            label={point.unit === null ? "Wert" : `Wert in ${point.unit}`}
            inputMode="decimal"
            value={draft}
            disabled={sent}
            onChange={(event) => setDraft(event.target.value)}
            hint={`Wert ${point.key}`}
          />
          {write.isError && <Alert tone="danger">{write.error.message}</Alert>}
          {sent && (
            <Alert tone="info">
              Abgeschickt. Die Quittung des Geräts steht unter „Einstellungen" bei den Aufträgen;
              der neue Wert erscheint mit der nächsten Meldung.
            </Alert>
          )}
        </div>
      </Dialog>
    </>
  );
}
